import React, { useState, useEffect } from "react";
import APICall from "../Utility/APICall";
import { paths } from "../Utility/Constants";
import { useAuth } from "../Utility/AuthProvider";
import Lottie from "lottie-react";
import loaderAnimation from "../assets/animations/loader.json";

function CollectionsBuildingWise() {
  const [loading, setLoading] = useState(true);
  const [buildingRentData, setBuildingRentData] = useState([]);
  const [currentMonth, setCurrentMonth] = useState("");
  const [summaryTotals, setSummaryTotals] = useState(null);
  const { jwt } = useAuth();

  // This `useEffect` hook for fetching data is correct and remains unchanged.
  useEffect(() => {
    const fetchBuildingRentSummary = async () => {
      setLoading(true);
      try {
        const response = await APICall(
          "GET",
          paths.building.buildingRentSummary,
          undefined,
          undefined,
          jwt
        );
        console.log("Building Rent Summary Response:", response);

        if (response && response.status === "success" && response.response) {
          setBuildingRentData(response.response.buildings || []);
          setCurrentMonth(response.response.current_month || "");
          setSummaryTotals(response.response.summary || null);
        } else {
          setBuildingRentData([]);
          setSummaryTotals(null);
          setCurrentMonth("");
        }
      } catch (error) {
        console.error("Error fetching building rent summary:", error);
        setBuildingRentData([]);
        setSummaryTotals(null);
        setCurrentMonth("");
      } finally {
        setLoading(false);
      }
    };

    fetchBuildingRentSummary();
  }, [jwt]);

  // --- Helper to format currency (no change) ---
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // The `toggleBuildingDetails`, `ImageGallery`, and `getImageUrl` helpers are no longer needed and have been removed.

  // --- Render Logic ---
  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-96">
        <Lottie
          animationData={loaderAnimation}
          loop={true}
          className="w-24 h-24"
        />
        <span className="text-gray-600 mt-2 font-medium">
          Loading Building Summary...
        </span>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold text-gray-800">
          Building-Wise Rent Summary
        </h1>
        {currentMonth && (
          <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full">
            {currentMonth}
          </span>
        )}
      </div>

      {buildingRentData.length === 0 ? (
        <div className="p-6 text-center text-gray-500 bg-gray-50 rounded border">
          No building collection data available for{" "}
          {currentMonth || "the current period"}.
        </div>
      ) : (
        <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              {/* --- 1. UPDATED THE TABLE HEADER --- */}
              <thead className="bg-gray-100">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Sr. No.
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Building Name
                  </th>
                  {/* New column for Agreement Count */}
                  <th
                    scope="col"
                    className="px-6 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Agreements
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Expected Rent
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Received Rent
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Due Amount
                  </th>
                  {/* "Actions" column header removed */}
                </tr>
              </thead>
              {/* --- 2. UPDATED THE TABLE BODY --- */}
              <tbody className="bg-white divide-y divide-gray-200">
                {buildingRentData.map((building, index) => {
                  const dueAmount =
                    (Number(building.total_expected_rent) || 0) -
                    (Number(building.total_received_rent) || 0);

                  return (
                    // The React.Fragment and expandable row are removed.
                    <tr
                      key={building.building_id}
                      className="hover:bg-gray-50 transition-colors duration-150 ease-in-out"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-700">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {building.building_name}
                      </td>
                      {/* New cell for Agreement Count */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-700">
                        {building.agreements_count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatCurrency(building.total_expected_rent || 0)}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${(Number(building.total_received_rent) || 0) > 0 ? "text-green-600" : "text-gray-700"}`}
                      >
                        {formatCurrency(building.total_received_rent || 0)}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${dueAmount > 0 ? "text-red-600" : "text-gray-700"}`}
                      >
                        {formatCurrency(dueAmount)}
                      </td>
                      {/* "Actions" cell with button removed */}
                    </tr>
                  );
                })}
              </tbody>
              {/* --- 3. UPDATED THE TABLE FOOTER --- */}
              {summaryTotals && (
                <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                  <tr>
                    <td
                      colSpan="2"
                      className="px-6 py-3 text-left text-sm font-bold text-gray-700"
                    >
                      Totals
                    </td>
                    <td className="px-6 py-3"></td>
                    {/* Placeholder for Agreements column */}
                    <td className="px-6 py-3 text-left text-sm font-bold text-gray-700">
                      {formatCurrency(summaryTotals.total_expected_rent || 0)}
                    </td>
                    <td
                      className={`px-6 py-3 text-left text-sm font-bold ${summaryTotals.total_received_rent > 0 ? "text-green-700" : "text-gray-700"}`}
                    >
                      {formatCurrency(summaryTotals.total_received_rent || 0)}
                    </td>
                    <td
                      className={`px-6 py-3 text-left text-sm font-bold ${summaryTotals.total_pending_rent > 0 ? "text-red-700" : "text-gray-700"}`}
                    >
                      {formatCurrency(summaryTotals.total_pending_rent || 0)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default CollectionsBuildingWise;
